import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const AlliancePage = () => {
  const benefits = [
    { title: 'Regional Expertise', description: '10 premier independent agencies across Pennsylvania providing local knowledge with regional strength in Bucks County, Montgomery County, and beyond', icon: 'ri-map-pin-line' },
    { title: 'National Broker Capabilities', description: 'Resources and sophistication typically found only with national brokers, delivered locally across Pennsylvania', icon: 'ri-global-line' },
    { title: 'Top Insurance Relationships', description: "Preferred relationships with the region and nation's top insurance companies", icon: 'ri-handshake-line' },
    { title: 'Extensive Resources', description: 'Over 250 employees providing comprehensive service and expertise throughout Pennsylvania', icon: 'ri-team-line' },
    { title: 'Proven Track Record', description: '$310+ Million in Property-Casualty premiums written in 2021', icon: 'ri-line-chart-line' },
    { title: 'Industry Leadership', description: 'Ranked 19th largest agency network in the nation', icon: 'ri-trophy-line' },
  ];

  const members = [
    'The Stoll Agency, Inc.', 'Regional Insurance Partners', 'Commonwealth Insurance Group',
    'Keystone Risk Solutions', 'Liberty Insurance Associates', 'Heritage Insurance Services',
    'Premier Coverage Advisors', 'Alliance Insurance Group', 'Pennsylvania Risk Management', 'Strategic Insurance Partners',
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-green-400 text-sm font-semibold tracking-widest uppercase mb-4">Pennsylvania Insurance Network</p>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">The Alliance</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Proud member of The Insurance Alliance Network — serving Bucks County, Montgomery County, and all of Pennsylvania
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Strength Through Partnership</h2>
            <div className="w-24 h-1 bg-green-500 mx-auto mb-8"></div>
            <p className="text-lg text-slate-300 leading-relaxed mb-6">
              Effective June 1, 2017, The Stoll Agency, Inc. became the newest member of The
              Insurance Alliance. This partnership enhances our ability to serve clients in <strong className="text-white">Bucks County</strong>, <strong className="text-white">Montgomery County</strong>, and throughout Pennsylvania with even greater resources and capabilities.
            </p>
            <p className="text-lg text-slate-300 leading-relaxed">
              The Insurance Alliance is a collective of 10 premier independent agencies located in
              various regions of Pennsylvania. Through size, diversity and broad expertise, The
              Alliance offers on a regional level that which is normally found only with national
              brokers — ranking in 2021 as the 19th largest agency network in the nation.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-20 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Alliance Benefits</h2>
            <div className="w-24 h-1 bg-green-500 mx-auto"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-slate-900 border border-slate-700 rounded-2xl p-8 hover:border-green-500/50 hover:shadow-xl hover:shadow-green-900/20 transition-all duration-300 hover:-translate-y-1">
                <div className="w-16 h-16 bg-green-500/10 border border-green-500/20 rounded-xl flex items-center justify-center mb-6">
                  <i className={`${benefit.icon} text-3xl text-green-400`}></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{benefit.title}</h3>
                <p className="text-slate-400 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { value: '19th', label: 'Largest Agency Network' },
              { value: '10', label: 'Premier PA Agencies' },
              { value: '250+', label: 'Employees' },
              { value: '$310M+', label: 'Premiums Written' },
            ].map((stat, i) => (
              <div key={i} className="text-center bg-slate-800 border border-slate-700 rounded-2xl p-8">
                <div className="text-5xl font-bold text-green-400 mb-2">{stat.value}</div>
                <div className="text-slate-400 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Member Agencies */}
      <section className="py-20 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Alliance Members</h2>
            <div className="w-24 h-1 bg-green-500 mx-auto mb-6"></div>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto">
              A network of Pennsylvania's most respected independent insurance agencies
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {members.map((member, index) => (
              <div key={index} className="bg-slate-900 border border-slate-700 rounded-xl p-6 hover:border-green-500/50 transition-all flex items-center">
                <div className="w-12 h-12 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-building-line text-2xl text-green-400"></i>
                </div>
                <span className="text-white font-semibold">{member}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-8 text-center">What This Means for You</h2>
          <div className="space-y-6">
            {[
              { title: 'Enhanced Resources', text: 'As a member of The Insurance Alliance, we have access to greater resources, technology, and expertise while maintaining our personal, local service approach for Bucks County and Montgomery County clients.' },
              { title: 'Better Coverage Options', text: 'Our alliance relationships give us preferred access to top insurance carriers, resulting in more competitive pricing and comprehensive coverage options for Pennsylvania contractors.' },
              { title: 'Same Personal Service', text: 'Despite our enhanced capabilities, we remain the same family-owned business committed to providing personalized service and building lasting relationships with our clients throughout Pennsylvania.' },
            ].map((item, i) => (
              <div key={i} className="bg-slate-800 border border-slate-700 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-white mb-4">{item.title}</h3>
                <p className="text-slate-300 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-green-700 to-green-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Learn More About The Alliance</h2>
          <p className="text-xl text-white/80 mb-10">Visit The Insurance Alliance website for more information</p>
          <a
            href="https://www.theinsurancealliancenetwork.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-10 py-4 bg-white text-green-700 rounded-full font-semibold text-lg hover:bg-gray-100 transition-all shadow-lg whitespace-nowrap"
          >
            Visit Alliance Website
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AlliancePage;
